import React, { useCallback, useState, useEffect, useRef, } from "react";
import { View, Text, ScrollView, ImageBackground, Modal, Animated, Pressable, Image, Switch, Dimensions, ActivityIndicator, useWindowDimensions, TextInput } from "react-native";
import { SketchPicker } from "react-color";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app } from "../firebase";
import { getFirestore, doc, getDoc, setDoc, updateDoc, query, where, getDocs, collection, writeBatch, onSnapshot } from "firebase/firestore";
import axios from "axios";
import { useUser } from "../Widgets/UserContext";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { usePageTracking } from '../Hooks/usePageTracking';
import { useTutorial } from "./Helpers/Tutorials";
import { debounce } from "lodash";
import { isCardEmpty, validateWidget } from '../services/WidgetWriteGuard';
import styles from "../Styles/EventsStyles";
import ImageGallery from "./EventsWidgets/ImageGallery";
import TextBox from "./EventsWidgets/TextBox";
import RSVP from "./EventsWidgets/RSVP";
import PictureFrame from "./EventsWidgets/PictureFrame";
import URLPreview from "./EventsWidgets/URLPreview";
import Poll from "../Widgets/Poll";

import HeaderBar, { useScrollHide } from "../Widgets/HeaderBar";
import GraduationCard from "../Widgets/GraduationCard";
import CombinedCard from "../Widgets/CombinedCard";
import EventCard from "../Widgets/EventCard"; // Import EventCard
import { BackgroundProvider } from './Helpers/BackgroundContext';
import PrivatePageMessage from "./Helpers/PrivatePageMessage"; // Import the new component
import PageUnavailableMessage from "./Helpers/PageUnavailableMessage";
import Countdown from "./EventsWidgets/Countdown";
import { HEADER_PADDING } from "../constants";
import { getBaseUrl } from "../config";
import { MYTASSEL_PLUS_FEATURES } from "../constants/MyTasselPlus";
import useMyTasselPlus from "../Hooks/useMyTasselPlus";
import {
	FALLBACK_STANDARD_PROFILE_BACKGROUNDS,
	STANDARD_PROFILE_BACKGROUNDS_CONFIG,
	filterProfileBackgroundsForTier,
	getImageSource,
	getStandardProfileBackgroundValue,
	normalizeStandardProfileBackgrounds,
} from "../services/StandardProfileBackgroundService";
import {
	checkMyTasselPlusSlugAvailability,
	extractMyTasselRouteSegment,
	extractMyTasselVanitySlug,
	claimMyTasselPlusSlug,
	normalizeMyTasselPlusSlug,
	resolveMyTasselRouteUid,
} from "../services/VanitySlugService";
// import { buttonHoverStyle, imageHoverStyle } from "../../utils/hoverStyles";
import { TutorialPopup } from "./Helpers/Tutorials";
import { spotlightTarget, widgetOptionTarget, SPOTLIGHT_TARGETS } from "../Widgets/SpotlightTutorial/spotlightTargets";
import { emitSpotlightEvent, useEmitSpotlightEventWhenOpened, useSpotlightSignals } from "../Widgets/SpotlightTutorial/spotlightBus";
import { SPOTLIGHT_EVENTS, SPOTLIGHT_SIGNAL_SCOPES } from "../Widgets/SpotlightTutorial/spotlightEventNames";
import { getAvailableWidgetOptions } from "../constants/WidgetTypes";
import SEOHead from '../components/SEOHead';

import VisitorViewBanner from './Components/VisitorViewBanner';
import { EventStructuredData } from '../components/StructuredData';

// Check if in development mode for testing features
const isDevelopment = process.env.NODE_ENV === "development";



const STANDARD_FONT_COLORS = [
	{ label: "Black", value: "#000000" },
	{ label: "White", value: "#FFFFFF" },
	{ label: "Red", value: "#FF0000" },
	{ label: "Green", value: "#00AA00" },
	{ label: "Blue", value: "#0000FF" },
	{ label: "Yellow", value: "#FFD700" },
];

const getBackgroundKey = (background) => background?.id || background?.key || "";

const getLegacyBackgroundKey = (background) => {
	const match = String(background?.imageUrl || "").match(/^builtin:exBackground([1-5])$/);
	return match ? `background-${match[1]}` : "";
};

const backgroundMatchesKey = (background, key) => {
	if (!key) return false;
	return [getBackgroundKey(background), background?.key, getLegacyBackgroundKey(background)]
		.filter(Boolean)
		.includes(key);
};

const getProfileBackground = (backgrounds, key) => (
	backgrounds.find((background) => backgroundMatchesKey(background, key)) ||
	backgrounds[0] ||
	FALLBACK_STANDARD_PROFILE_BACKGROUNDS[0]
);

const getStandardFontColor = (value = "") => (
	STANDARD_FONT_COLORS.find((color) => color.value.toLowerCase() === String(value).toLowerCase())
);

const formatPersonalizedUrlDate = (value) => {
	if (!value) return "";
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" }).format(date);
};

// Force Firebase Functions for testing metadata endpoint
const SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api/fetch-metadata";

const Events = ({ route }) => {
	//#region - Variables
	const { width, height } = useWindowDimensions();
	const isMobile = width < 768;
	const isTablet = width >= 768 && width < 1024;
	const isSmallDesktop = width >= 1024 && width < 1440;
	const isLargeDesktop = width >= 1440;


	const {
		setShowTutorial,
		setActivePopupId,
		showTutorial,
		isLoading,
		tutorialsToShow,
		setClosedTutorials,
		setTutorialsToShow,
		activePopupId
	} = useTutorial();

	const fontFamilies = {
		Bodoni72Smallcaps: "Bodoni72Smallcaps",
	};

	// Initialize Firestore
	const db = getFirestore(app);
	const { currentUser, currentUserData, authLoading } = useUser();
	const myTasselPlus = useMyTasselPlus();
	const [pageOwner, setPageOwner] = useState(null);
	const navigation = useNavigation();
	const auth = getAuth(app);
	const [pageOwnerId, setPageOwnerId] = useState(null); // Add state for pageOwnerId
	const [isPageOwnerReady, setIsPageOwnerReady] = useState(false);
	const [pageNotFound, setPageNotFound] = useState(false);
	const [pageNotFoundReason, setPageNotFoundReason] = useState('not_found');
	const [showLogoutModal, setShowLogoutModal] = React.useState(false);
	const [isOwner, setIsOwner] = useState(false); // New state to handle page ownership
	const [loading, setLoading] = useState(true); // New loading state

	// Get uid from route params or URL
	const uid = route?.params?.uid || new URLSearchParams(window.location.search).get('uid');

	// Track page view with specific user events
	const ownerDisplayName = React.useMemo(() => {
		if (!uid) return 'Events (Generic)';
		
		return pageOwner?.firstName && pageOwner?.lastName 
			? `${pageOwner.firstName} ${pageOwner.lastName}`
			: pageOwner?.email || pageOwner?.displayName || `User ${uid}`;
	}, [uid, pageOwner]);
	
	usePageTracking(uid ? `Events-${uid}` : 'Events', { 
		userId: currentUser?.uid || 'anonymous',
		eventsOwner: uid,
		ownerDisplayName: uid ? `User ${uid}` : 'Events (Generic)', // Always use User ID for privacy
		viewerType: currentUser?.uid === uid ? 'owner' : 'visitor',
		pageContext: `Events of User ${uid || 'Generic'}`,
		specificPage: uid ? `Events-${uid}` : 'Events'
	});
	const scrollViewRef = useRef(null);
	const scrollOffsetRef = useRef(0);
	const [hasChanges, setHasChanges] = useState(false); // New state to track changes
	const shouldOpenAsVisitor = String(route?.params?.viewAsVisitor) === "true"; // e.g. "Preview My Page" at the end of the tutorial
	const [isVisitorView, setIsVisitorView] = useState(shouldOpenAsVisitor); // New state for viewing mode
	useEffect(() => {
		if (shouldOpenAsVisitor) setIsVisitorView(true);
	}, [shouldOpenAsVisitor]);
	const [cardBackground, setCardBackground] = useState(null); // State for card background
	const [isAddingWidget, setIsAddingWidget] = useState(false); // Lock to prevent concurrent additions
	const [removingWidgetIds, setRemovingWidgetIds] = useState(new Set()); // Track widgets being removed
	const previousAuthState = useRef(null); // Track previous auth state to prevent unnecessary reloads

	const [loadingMessage, setLoadingMessage] = useState(""); // State for loading message
	const [successMessage, setSuccessMessage] = useState(""); // State for success message
	const [errorMessage, setErrorMessage] = useState(""); // State for error message

	const [alertShown, setAlertShown] = useState(false); // Add state to track alert display

	// Add state for authorization
	const [isAuthorized, setIsAuthorized] = useState(false);
	const [isAuthorizationLoading, setIsAuthorizationLoading] = useState(true); // New state for authorization loading
	const [hoveredButton, setHoveredButton] = useState(null);
	
	// Feature flags
	const [featureFlags, setFeatureFlags] = useState({
		showPollsWidget: false,
		showEditorMap: false,
		showVisitorMap: false,
	});

	const { headerTranslateY, onScroll } = useScrollHide('scrollView');
	//#endregion

	//#region - Testing Only Development
	const { setCurrentUser, setCurrentUserData } = useUser();

	// Fetch feature flags - with environment-specific support
	useEffect(() => {
		const fetchFeatureFlags = async () => {
			try {
				// Determine current environment
				const isProduction = typeof window !== 'undefined' && window.location?.hostname === 'mytassel.com';
				const env = isProduction ? 'production' : 'staging';
				
				const configRef = doc(db, 'config', 'features');
				const configSnap = await getDoc(configRef);
				if (configSnap.exists()) {
					const flags = configSnap.data();
					
					// Helper to get flag value based on environment
					const getFlagValue = (flag) => {
						if (typeof flag === 'boolean') return flag; // Legacy format
						return flag?.[env] ?? false; // New format: { staging: bool, production: bool }
					};
					
					setFeatureFlags({
						showPollsWidget: getFlagValue(flags.showPollsWidget),
						showEditorMap: getFlagValue(flags.showEditorMap),
						showVisitorMap: getFlagValue(flags.showVisitorMap),
					});
				}
			} catch (error) {
				// Keep defaults if fetch fails
			}
		};
		fetchFeatureFlags();
	}, [db]);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (authUser) => {
			if (authUser) {
				// Update the user in the context
				setCurrentUser(authUser);
				// Optionally update user data from Firestore
			} else {
				setCurrentUser(null);
				setCurrentUserData(null);
			}
		});

		return () => unsubscribe(); // Cleanup subscription
	}, []);

	useEffect(() => {
		if (isDevelopment) {
			const testLogin = async () => {
				try {
					const testEmails = [
						"Tjhawk27@gmail.com", // Trevor Hawkins
						"trevorhawkinsofficial@gmail.com", // Olivia Nichols
						"trevorhawkins@makenoisemedia.io", // Sam Minix
						"info@dsignable.com" // Tristan
					];
					const password = "Newtjhawk27";
					const lowerCaseEmail = testEmails[3].toLowerCase(); // Default to Jennifer

					const userCredential = await signInWithEmailAndPassword(auth, lowerCaseEmail, password);
					const user = userCredential.user;

					const userDocRef = doc(db, "users", lowerCaseEmail);
					const userDocSnap = await getDoc(userDocRef);

					if (userDocSnap.exists()) {
						const userData = userDocSnap.data();
						setCurrentUser(user); // Set the auth user
						setCurrentUserData(userData); // Set the Firestore user data
					}
				} catch (error) {
					// Silent fail for test login
				}
			};
			// testLogin();
		}
	}, []);

	useEffect(() => {
		const loadCards = async () => {
			// Skip reload if user just went from not logged in to anonymously logged in
			// (happens during guest RSVP submission - we don't want to reload the page)
			const isAnonymousSignIn = !previousAuthState.current && currentUser?.isAnonymous;
			if (isAnonymousSignIn) {
				previousAuthState.current = currentUser;
				setLoading(false);
				return;
			}
			previousAuthState.current = currentUser;
			
			setCards([]);
			setLoading(true);
			setLoadingMessage("");
			
			window.updateParentCards = (widgetIndex, updatedCard) => {
				if (isDevelopment) console.log('🔄 [updateParentCards] Called with index:', widgetIndex, 'card:', updatedCard?.id, 'type:', updatedCard?.type);
				setCards(currentCards => {
					const newCards = [...currentCards];
					if (updatedCard && updatedCard.id) {
						const found = newCards.findIndex(c => c.id === updatedCard.id);
						if (found !== -1) {
							newCards[found] = updatedCard;
							return newCards;
						}
					}
					if (typeof widgetIndex === 'number' && widgetIndex >= 0 && widgetIndex < newCards.length) {
						newCards[widgetIndex] = updatedCard;
						return newCards;
					}
					if (updatedCard) newCards.push(updatedCard);
					return newCards;
				});
			};
			
			try {
				if (!currentUser?.email) {
					setLoading(false);
					return;
				}
				
				if (uid && uid !== currentUser.uid) {
					setLoading(false);
					return;
				}
				
				const userEmail = currentUser.email.toLowerCase();
				const userDocRef = doc(db, "users", userEmail);
				const userDoc = await getDoc(userDocRef);

				if (userDoc.exists()) {
					const userData = userDoc.data();
					if (userData.pages && userData.pages.eventsPageConfig && userData.pages.eventsPageConfig.cards) {
					let cards = userData.pages.eventsPageConfig.cards;
					if (isDevelopment) console.log('📋 Loading cards from Firestore for email:', userEmail);
					
					let needsRepair = false;
					const repairedCards = cards.map((c, i) => {
						if (c.type && typeof c.type !== 'string') {
							if (isDevelopment) console.warn(`🔧 Card ${i} has corrupted type (${typeof c.type}) — attempting auto-repair`);
								needsRepair = true;
								let repairedType = '';
								if (c.eventTitle || c.eventDate || c.eventLocation || c.rsvpEnabled) {
									repairedType = 'Event';
								} else if (c.images && Array.isArray(c.images)) {
									repairedType = c.images.length > 1 ? 'ImageGallery' : 'PictureFrame';
								} else if (c.url) {
									repairedType = 'URLPreview';
								} else if (c.content) {
									repairedType = 'TextBox';
								} else if (c.question) {
									repairedType = 'Poll';
								} else if (c.going || c.maybe || c.decline) {
									repairedType = 'RSVP';
								} else if (c.targetDate) {
									repairedType = 'Countdown';
								}
								
							if (repairedType) {
								if (isDevelopment) console.log(`✅ Auto-repaired card ${i} type to: ${repairedType}`);
								return { ...c, type: repairedType };
							} else {
								if (isDevelopment) console.error(`❌ Cannot auto-repair card ${i} — removing from array`);
								return null;
							}
						}
						return c;
					}).filter(c => c !== null);
					
					if (needsRepair) {
						if (isDevelopment) console.log('💾 Writing repaired cards back to Firestore...');
						const userDocRef = doc(db, "users", userEmail);
						updateDoc(userDocRef, {
							'pages.eventsPageConfig.cards': repairedCards
						}).then(() => {
							if (isDevelopment) console.log('✅ Corrupted widgets auto-repaired and saved');
						}).catch(err => {
							if (isDevelopment) console.error('❌ Failed to save repaired cards:', err);
						});
						}
						
						setCards(repairedCards);
					}
				}
			} catch (error) {
				setErrorMessage("Failed to load cards. Please try again.");
				setTimeout(() => setErrorMessage(""), 2000);
			} finally {
				setLoading(false);
			}
		};

		loadCards();
	}, [currentUser, uid]);
	//#endregion

	//#region - Page Creation Variables
	const [editMode, setEditMode] = useState(false);
	const [editableMode, setEditableMode] = useState(false); // Control the edit mode
	const [cards, setCards] = useState([]); // State to handle the cards
	const [eventCards, setEventCards] = useState([]); // State to manage the event cards

	// Define saveError state and its setter function
	const [saveError, setSaveError] = useState("");

	// Additional states for background customization
	const [showBackgroundModal, setShowBackgroundModal] = useState(false); // New state for background modal visibility
	const [backgroundImage, setBackgroundImage] = useState(null); // Store the background image
	const [applyBlur, setApplyBlur] = useState(false); // Toggle for blur effect
	const [textColor, setTextColor] = useState("#000000"); // Store the text color
	const [selectedColor, setSelectedColor] = useState("#000000"); // State for the selected color
	const [profileBackgrounds, setProfileBackgrounds] = useState(FALLBACK_STANDARD_PROFILE_BACKGROUNDS);
	const [selectedStandardBackgroundKey, setSelectedStandardBackgroundKey] = useState(getBackgroundKey(FALLBACK_STANDARD_PROFILE_BACKGROUNDS[0]));
	const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
	const [slugInput, setSlugInput] = useState("");
	const [slugStatus, setSlugStatus] = useState({ checking: false, saving: false, available: false, error: "", success: "", changeAvailableAt: null, aliasExpiresAt: null });
	const [selectedColorSource, setSelectedColorSource] = useState("standard");
	const [selectedWidgetType, setSelectedWidgetType] = useState(""); // State for the selected widget type
	const [showEmptyWidgetDropdown, setShowEmptyWidgetDropdown] = useState(false); // Dropdown for empty-state widget picker
	useEmitSpotlightEventWhenOpened(showBackgroundModal, SPOTLIGHT_EVENTS.ANNOUNCEMENT_PAGE_SETTINGS_OPENED);
	useEmitSpotlightEventWhenOpened(showEmptyWidgetDropdown, SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_PICKER_OPENED);
	useSpotlightSignals(SPOTLIGHT_SIGNAL_SCOPES.ANNOUNCEMENT, {
		availableWidgetTypes: getAvailableWidgetOptions(featureFlags).map((option) => option.value).join(","),
	});
	const [widgets, setWidgets] = useState([]); // State to handle widgets
	//#endregion

	const normalizedSlugPreview = normalizeMyTasselPlusSlug(slugInput);
	const publicUrlPreview = `${getBaseUrl().replace(/\/$/, "")}/${normalizedSlugPreview || "your-name"}`;
	const currentSavedSlug = normalizeMyTasselPlusSlug(pageOwner?.myTasselPlus?.slug || "");
	const isChangingSavedSlug = Boolean(currentSavedSlug && normalizedSlugPreview && normalizedSlugPreview !== currentSavedSlug);
	const legacyAnnouncementPath = pageOwnerId ? `/events/${pageOwnerId}` : "/events/G#####";
	const legacyRegistryPath = pageOwnerId ? `/registry/${pageOwnerId}` : "/registry/G#####";
	const graduateName = [pageOwner?.firstName, pageOwner?.lastName].filter(Boolean).join(" ") || pageOwner?.displayName || pageOwner?.firstName || "Graduate";
	const profileBackgroundChoices = React.useMemo(
		() => filterProfileBackgroundsForTier(profileBackgrounds, myTasselPlus.hasPlus),
		[profileBackgrounds, myTasselPlus.hasPlus]
	);
	const defaultProfileBackground = profileBackgroundChoices[0] || FALLBACK_STANDARD_PROFILE_BACKGROUNDS[0];

	useEffect(() => {
		const configRef = doc(
			db,
			STANDARD_PROFILE_BACKGROUNDS_CONFIG.collection,
			STANDARD_PROFILE_BACKGROUNDS_CONFIG.id
		);

		const unsubscribe = onSnapshot(
			configRef,
			(configSnap) => {
				const configured = configSnap.exists()
					? normalizeStandardProfileBackgrounds(
						configSnap.data()?.[STANDARD_PROFILE_BACKGROUNDS_CONFIG.field] || []
					)
					: [];
				setProfileBackgrounds(configured.length > 0 ? configured : FALLBACK_STANDARD_PROFILE_BACKGROUNDS);
			},
			() => {
				setProfileBackgrounds(FALLBACK_STANDARD_PROFILE_BACKGROUNDS);
			}
		);

		return unsubscribe;
	}, [db]);

	useEffect(() => {
		const pageConfig = pageOwner?.pages?.eventsPageConfig;
		if (!pageConfig || pageConfig.eventsCustomBackground) return;

		const standardBackground = getProfileBackground(profileBackgroundChoices, pageConfig.eventsStandardBackgroundKey);
		setSelectedStandardBackgroundKey(getBackgroundKey(standardBackground));
		setBackgroundImage(getStandardProfileBackgroundValue(standardBackground));
	}, [
		pageOwner?.pages?.eventsPageConfig?.eventsCustomBackground,
		pageOwner?.pages?.eventsPageConfig?.eventsStandardBackgroundKey,
		profileBackgroundChoices,
	]);

	useEffect(() => {
		if (!showBackgroundModal || !myTasselPlus.hasPlus) return;

		const normalizedSlug = normalizeMyTasselPlusSlug(slugInput);
		if (!normalizedSlug) {
			setSlugStatus({ checking: false, saving: false, available: false, error: "", success: "", changeAvailableAt: null, aliasExpiresAt: null });
			return;
		}

		let cancelled = false;
		setSlugStatus({ checking: true, saving: false, available: false, error: "", success: "", changeAvailableAt: null, aliasExpiresAt: null });

		const timeoutId = setTimeout(async () => {
			try {
				const result = await checkMyTasselPlusSlugAvailability(currentUser, normalizedSlug);
				if (cancelled) return;
				setSlugStatus({
					checking: false,
					saving: false,
					available: result.available === true,
					error: "",
					success: result.available ? "Available." : "",
					changeAvailableAt: result.changeAvailableAt || null,
					aliasExpiresAt: result.previousSlugAliasExpiresAt || null,
				});
			} catch (error) {
				if (cancelled) return;
				setSlugStatus({
					checking: false,
					saving: false,
					available: false,
					error: error.message || "Could not check personalized URL.",
					success: "",
					changeAvailableAt: error.data?.changeAvailableAt || null,
					aliasExpiresAt: null,
				});
			}
		}, 450);

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
		};
	}, [currentUser, myTasselPlus.hasPlus, showBackgroundModal, slugInput]);

	//#region - Page Creation Functions
	// Function to add a new card
	const addCard = async (type) => {
		// Prevent concurrent additions (race condition protection)
		if (isAddingWidget) {
		if (isDevelopment) console.warn('➕ [addCard] BLOCKED — Already adding a widget, please wait');
		return;
	}
	
	if (isDevelopment) console.log('➕ [addCard] Adding widget of type:', type);
		setIsAddingWidget(true);
		
		try {
			const userEmail = currentUser.email.toLowerCase();
			const userDocRef = doc(db, "users", userEmail);
			
			// Create a new card with appropriate initial state based on type
			const newCard = {
				id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // More unique ID
				type,
				content: "",
				position: cards.length, // Use current local state length
				// Initialize type-specific state
				...(type === "Countdown" && {
					targetDate: new Date().toISOString(),
					onCompleteMessage: "The event has started!",
				}),
				...(type === "RSVP" && {
					rsvp: "",
					going: [],
					maybe: [],
					decline: []
				}),
				...(type === "Event" && {
					eventTitle: "",
					eventDate: "",
					eventLocation: "",
					eventLocationCoords: null,
					eventDescription: "",
					rsvpEnabled: false,
					reminderSettings: { enabled: false, reminderTimes: [] },
					going: [],
					maybe: [],
					decline: [],
					// New Event cards always start as incomplete — cleared when owner fills in title + date.
					status: 'incomplete',
					needsReview: true,
				}),
				events: [],
				metadata: {}
			};

			// OPTIMISTIC UPDATE: Add to local state immediately for instant UI feedback
			const optimisticCards = [...cards, newCard];
		setCards(optimisticCards);
		if (isDevelopment) console.log('➕ [addCard] 🔄 Optimistic update — new card added to UI');

			if (isPreviewingAsOwner) {
				setHasChanges(false);
				showPreviewNotice("Widget added in preview. Changes are not saved.");
				if (scrollViewRef.current) {
					setTimeout(() => {
						try {
							scrollViewRef.current.scrollToEnd({ animated: true });
						} catch (e) {
							// ignore if not supported
						}
					}, 50);
				}
				return;
			}

			// Background: Fetch latest from Firestore and merge
			const userDocSnap = await getDoc(userDocRef);
			const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
			const firestoreCards = existingData.pages?.eventsPageConfig?.cards || [];
			
			// Merge: If the new card's ID doesn't exist in Firestore, add it
			const cardExists = firestoreCards.some(c => c.id === newCard.id);
			const mergedCards = cardExists ? firestoreCards : [...firestoreCards, newCard];
			const cleanedCards = cleanForFirestore(mergedCards);

			// Write merged data to Firestore
			await setDoc(userDocRef, {
				pages: {
					...existingData.pages,
					eventsPageConfig: {
						...existingData.pages?.eventsPageConfig,
						cards: cleanedCards
					}
				}
			}, { merge: true });

			// Sync local state with what we actually saved to Firestore
			setCards(cleanedCards);
			setHasChanges(false);
			if (scrollViewRef.current) {
				setTimeout(() => {
					try {
						scrollViewRef.current.scrollToEnd({ animated: true });
					} catch (e) {
						// ignore if not supported
					}
				}, 50);
			}
		} catch (error) {
			if (isDevelopment) console.error('➕ [addCard] ❌ Failed:', error);
			try {
				const userEmail = currentUser.email.toLowerCase();
				const userDocRef = doc(db, "users", userEmail);
				const userDoc = await getDoc(userDocRef);
				const userData = userDoc.data();
				const firestoreCards = userData?.pages?.eventsPageConfig?.cards || [];
				setCards(firestoreCards);
				if (isDevelopment) console.log('➕ [addCard] 🔄 Rolled back to Firestore state after error');
			} catch (rollbackError) {
				if (isDevelopment) console.error('➕ [addCard] ❌ Rollback failed:', rollbackError);
			}
		} finally {
			setIsAddingWidget(false);
		}
	};

	// Safely serialize data for Firestore — manual recursive deep-copy.
	const cleanForFirestore = (data) => {
		if (data === undefined || data === null) return null;
		const seen = new WeakSet();
		const clean = (val, depth) => {
			if (depth > 30) return null;
			if (val === undefined) return null;
			if (val === null) return null;
			if (typeof val === 'function' || typeof val === 'symbol') return null;
			if (typeof val !== 'object') return val;
			if (typeof HTMLElement !== 'undefined' && val instanceof HTMLElement) return null;
			if (val.$$typeof) return null;
			if (typeof val.then === 'function') return null;
			if (seen.has(val)) return null;
			seen.add(val);
			if (Array.isArray(val)) {
				const result = [];
				for (let i = 0; i < val.length; i++) {
					const item = clean(val[i], depth + 1);
					if (Array.isArray(item)) {
						for (let j = 0; j < item.length; j++) {
							if (item[j] !== null && item[j] !== undefined) result.push(item[j]);
						}
					} else if (item !== null && item !== undefined) {
						result.push(item);
					}
				}
				return result;
			}
			const out = {};
			let keys;
			try { keys = Object.keys(val); } catch (_) { return null; }
			for (let i = 0; i < keys.length; i++) {
				const k = keys[i];
				if (k.length > 4 && k.startsWith('__') && k.endsWith('__')) continue;
				try {
					const v = val[k];
					if (typeof v === 'function' || typeof v === 'symbol') continue;
					if (v && v.$$typeof) continue;
					const c = clean(v, depth + 1);
					if (c !== undefined) out[k] = c;
				} catch (_) { /* skip */ }
			}
			return out;
		};
		return clean(data, 0);
	};

	// Function to remove a card — writes directly to Firestore to ensure persistence
	const removeCard = async (id) => {
		// Prevent concurrent removals of the same widget (race condition protection)
		if (removingWidgetIds.has(id)) {
		if (isDevelopment) console.warn('🗑️ [removeCard] BLOCKED — Widget', id, 'is already being removed');
		return;
	}
	
	if (isDevelopment) console.log('🗑️ [removeCard] Removing widget id:', id);
		
		// Mark this widget as being removed
		setRemovingWidgetIds(prev => new Set(prev).add(id));
		
		try {
			const userEmail = currentUser.email.toLowerCase();
			const userDocRef = doc(db, "users", userEmail);

			// OPTIMISTIC UPDATE: Remove from local state immediately for instant UI feedback
			const optimisticCards = cards.filter(card => card.id !== id);
		setCards(optimisticCards);
		if (isDevelopment) console.log('🗑️ [removeCard] 🔄 Optimistic update — widget removed from UI');

		if (isPreviewingAsOwner) {
			showPreviewNotice("Widget removed in preview. Changes are not saved.");
			return;
		}

		const userDocSnap = await getDoc(userDocRef);
		const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
		const firestoreCards = existingData.pages?.eventsPageConfig?.cards || [];

		const updatedCards = firestoreCards.filter(card => card.id !== id);
		if (isDevelopment) console.log('🗑️ [removeCard] Firestore before:', firestoreCards.length, 'after:', updatedCards.length);

		const cleanedCards = cleanForFirestore(updatedCards);
		await updateDoc(userDocRef, {
			'pages.eventsPageConfig.cards': cleanedCards
		});
		if (isDevelopment) console.log('🗑️ [removeCard] ✅ Firestore updated');

			// Sync local state with what we actually saved to Firestore
			setCards(cleanedCards);
			setSuccessMessage("Widget removed successfully");
			setTimeout(() => setSuccessMessage(""), 3000);
	} catch (error) {
		if (isDevelopment) console.error("🗑️ [removeCard] ❌ Failed:", error);
		try {
			const userEmail = currentUser.email.toLowerCase();
			const userDocRef = doc(db, "users", userEmail);
			const userDoc = await getDoc(userDocRef);
			const userData = userDoc.data();
			const firestoreCards = userData?.pages?.eventsPageConfig?.cards || [];
			setCards(firestoreCards);
			if (isDevelopment) console.log('🗑️ [removeCard] 🔄 Rolled back to Firestore state after error');
		} catch (rollbackError) {
			if (isDevelopment) console.error('🗑️ [removeCard] ❌ Rollback failed:', rollbackError);
		}
			setErrorMessage("Failed to remove widget. Please try again.");
			setTimeout(() => setErrorMessage(""), 3000);
		} finally {
			// Remove the lock for this widget ID
			setRemovingWidgetIds(prev => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
		}
	};

	// Function to handle image upload for ImageGallery — writes directly to Firestore
	const handleImageGalleryUpload = async (event, cardId) => {
		const file = event.target.files[0];
		if (file && file.type.startsWith("image/")) {
			if (isPreviewingAsOwner) {
				const previewUrl = typeof URL !== 'undefined' ? URL.createObjectURL(file) : null;
				if (previewUrl) {
					setCards(currentCards => currentCards.map(card => {
						if (card.id !== cardId || card.type !== "ImageGallery") return card;
						const existingImages = card.content?.images || [];
						return {
							...card,
							content: {
								...card.content,
								images: [...existingImages, previewUrl],
							},
						};
					}));
				}
				showPreviewNotice("Image added in preview. Changes are not saved.");
				return;
			}

			setLoadingMessage("Uploading image...");
			const storagePath = `widgetsImages/${file.name}-${Date.now()}`;
			const storageRef = ref(getStorage(app), storagePath);

			try {
				const uploadResult = await uploadBytes(storageRef, file);
				const publicUrl = await getDownloadURL(uploadResult.ref);

				// Read fresh from Firestore, update by ID, clean, write back
				const userEmail = currentUser.email.toLowerCase();
				const userDocRef = doc(db, "users", userEmail);
				const userDocSnap = await getDoc(userDocRef);
				const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
				const existingCards = existingData.pages?.eventsPageConfig?.cards || [];

				const imgCardIdx = existingCards.findIndex(c => c.id === cardId);
				if (imgCardIdx !== -1 && existingCards[imgCardIdx].type === "ImageGallery") {
					const existingImages = existingCards[imgCardIdx].content?.images || [];
					existingCards[imgCardIdx] = {
						...existingCards[imgCardIdx],
						content: {
							...existingCards[imgCardIdx].content,
							images: [...existingImages, publicUrl],
						},
					};
				}

				const cleanedCards = cleanForFirestore(existingCards);
				await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': cleanedCards });
				setCards(cleanedCards);

				setLoadingMessage("");
				setSuccessMessage("Image uploaded successfully!");
				setTimeout(() => setSuccessMessage(""), 2000);
			} catch (error) {
				setLoadingMessage("");
				setErrorMessage("Failed to upload image. Please try again.");
				setTimeout(() => setErrorMessage(""), 2000);
			}
		}
	};

	// Memoized functions to prevent unnecessary re-renders
	const memoizedSaveWidget = useCallback((widget) => saveWidget(widget), []);
	const memoizedAddCard = useCallback((type) => addCard(type), []);

	// ?preview=owner enables owner-mode UI only for verified admins.
	// The uic param (also passed) handles authorization for private pages,
	// but owner-mode UI is gated on admin identity to prevent info leakage.
	const isAdminOwnerPreview = (() => {
		try {
			const params = new URLSearchParams(window.location.search);
			return params.get('preview') === 'owner' && currentUserData?.accountType === 'admin';
		} catch { return false; }
	})();
	const isPreviewingAsOwner = isAdminOwnerPreview && !isVisitorView;

	const showPreviewNotice = (message = "Preview updated. Changes are not saved.") => {
		setSuccessMessage(message);
		setTimeout(() => setSuccessMessage(""), 2500);
	};

	// Function to render each card
	const renderCards = () => {
		return cards.map((card, index) => {
			const effectiveIsOwner = (isOwner || isAdminOwnerPreview) && !isVisitorView;
			const isRemovingCard = removingWidgetIds.has(card.id);
			const showIncompleteNotice = effectiveIsOwner && card.needsReview;

			// Never render blank/invalid cards to non-owners.
			// isCardEmpty returns true when ALL required text fields are blank (severity === critical).
			if (!effectiveIsOwner && isCardEmpty(card)) return null;

				return (
				<View key={card.id} style={{ width: '100%' }}>
					{showIncompleteNotice && (
						<View style={{
							backgroundColor: '#fef3c7',
							borderTopWidth: 3,
							borderTopColor: '#f59e0b',
							borderLeftWidth: 3,
							borderLeftColor: '#f59e0b',
							borderRightWidth: 1,
							borderRightColor: '#fcd34d',
							borderBottomWidth: 1,
							borderBottomColor: '#fcd34d',
							borderRadius: 8,
							paddingHorizontal: 12,
							paddingVertical: 8,
							marginHorizontal: isMobile ? 12 : 20,
							marginBottom: 4,
							flexDirection: 'row',
							alignItems: 'center',
							gap: 8,
						}}>
							<Text style={{ fontSize: 14 }}>⚠️</Text>
							<Text style={{ color: '#92400e', fontSize: 12, fontWeight: '600', flex: 1 }}>
								This event has missing information and needs review. Tap to edit and complete it.
							</Text>
						</View>
					)}
					<EventCard
						card={card}
						onRemove={() => removeCard(card.id)}
						onSave={(widget) => saveWidget(widget)}
						onAdd={(type) => addCard(type)}
						isOwner={effectiveIsOwner}
						isVisitorView={isVisitorView}
						isPreviewMode={isPreviewingAsOwner}
						isLastCard={index === cards.length - 1}
						renderFetchMetadataButton={renderFetchMetadataButton}
						style={styles.eventCard}
						widgetIndex={index}
						currentUserData={currentUserData}
						pageOwner={pageOwner}
						pageOwnerId={pageOwnerId}
						pageTextColor={textColor}
						featureFlags={featureFlags}
						onImageUpload={(event) => handleImageGalleryUpload(event, card.id)}
						onRemoveImage={(imageUrl) => removeImageFromPictureFrame(card.id, imageUrl)}
						isAddingWidget={isAddingWidget}
						isRemovingWidget={isRemovingCard}
					/>
				</View>
			);
		});
	};

	// Debounce the save function — only saves card ORDER/position changes from local state.
	// Widget content saves are handled directly by EventCard.js/EventsWidget.js.
	const debouncedSavePageConfig = debounce(async (cardsToSave, currentUser, db) => {
		if (isPreviewingAsOwner) {
			if (isDevelopment) console.log('⏱️ [debouncedSave] SKIPPED — admin owner preview is local-only');
			return;
		}

		if (isDevelopment) console.log('⏱️ [debouncedSave] TRIGGERED — local cards:', cardsToSave.length);
		const userEmail = currentUser.email.toLowerCase();
		const userDocRef = doc(db, "users", userEmail);

		try {
			const userDocSnap = await getDoc(userDocRef);
			const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
			const firestoreCards = existingData.pages?.eventsPageConfig?.cards || [];

			const updatedCards = firestoreCards.map((fsCard, i) => {
				const localCard = cardsToSave.find(c => c.id === fsCard.id);
				if (localCard) {
					return { ...fsCard, position: localCard.position ?? i };
				}
				return fsCard;
			});

			const cleanedCards = cleanForFirestore(updatedCards);

			await setDoc(userDocRef, {
				pages: {
					...existingData.pages,
					eventsPageConfig: {
						...existingData.pages?.eventsPageConfig,
						cards: cleanedCards
					}
				}
			}, { merge: true });
			if (isDevelopment) console.log('⏱️ [debouncedSave] ✅ Done');

		} catch (error) {
			if (isDevelopment) console.error("⏱️ [debouncedSave] ❌ Failed:", error);
		}
	}, 1000);

	// Function to extract UID from the URL with retry logic
	const extractUIDFromURL = async (retryCount = 0) => {
		const url = window.location.href;
		// console.log(url);

		try {
			const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
			const routeSegment = extractMyTasselRouteSegment(pathname, 'events');
			const vanitySlug = extractMyTasselVanitySlug(pathname, 'events');
			if (routeSegment || vanitySlug) {
				const resolvedUid = await resolveMyTasselRouteUid({
					db,
					segment: routeSegment,
					slug: vanitySlug,
					pageType: 'events',
				});
				if (resolvedUid) return resolvedUid;
			}

			// Existing logic for 'username===' pattern
			if (url.includes("username===")) {
				const splitUrl = url.split("username===");
				// console.log(splitUrl);
				return splitUrl.length === 2 ? splitUrl[1] : null;
			}
		// Pattern for /events/GXXXXX or /events/FXXXXX (any letter-prefixed UID)
		else if (/\/events\/[A-Za-z]/.test(url)) {
			const regex = /\/events\/(\w+)/;
			const match = regex.exec(url);
			if (match && match.length > 1) {
				return match[1];
			}
		}
			// New logic to handle 'username=' parameter in query string
			else if (url.includes("?username=")) {
				const params = new URLSearchParams(url.split("?")[1]);
				const uid = params.get("username");
				// console.log("Extracted UID:", uid);
				return uid;
			}
			// Existing logic for other patterns
			else if (url.includes("?0")) {
				// console.log("url includes /?");
				const regex = /[?&]0=%3D&1=%3D&2=%3D&3=(\w+)&4=(\d+)&5=(\d+)&6=(\d+)&7=(\d+)&8=(\d+)/;
				const match = regex.exec(url);
				// console.log(match);
				if (match) {
					const uid = match.slice(1).join(""); // Concatenates the matched groups
					// console.log("Extracted UID:", uid);
					return uid;
				}
			}
		} catch (error) {
			// console.log("Error extracting UID:", error);
		}

		// If no UID is found and retry count is less than 1, wait and retry
		if (retryCount < 1) {
			// console.log("Retrying UID extraction in 1 second...");
			await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for 1 second
			return extractUIDFromURL(retryCount + 1); // Retry extraction
		}

		// console.log("Failed to extract UID after retrying.");
		return null; // Return null if UID extraction fails
	};

	// Function to load the page configuration from Firestore
	const currentUidRef = useRef(null);
	const loadPageConfig = useCallback(async () => {
		try {
		const uid = await extractUIDFromURL();

		if (!uid) {
			return;
		}

		// F-prefixed UIDs are always Friends & Family accounts — show unavailable message immediately
		if (/^F\d/i.test(uid)) {
			setPageNotFound(true);
			setPageNotFoundReason('wrong_account_type');
			setLoading(false);
			return;
		}

		// Only clear cards and reset state if we're navigating to a different page
			const isDifferentPage = currentUidRef.current !== uid;
			if (isDifferentPage) {
				setPageOwner(null);
				setCards([]);
				setBackgroundImage(null);
				setApplyBlur(false);
				setTextColor("#000000");
				setIsPageOwnerReady(false);
				setShowLogoutModal(false);
				setIsOwner(false);
				setHasChanges(false);
				setLoadingMessage("");
				setSuccessMessage("");
				setErrorMessage("");
				setAlertShown(false);
				setEditableMode(false);
				setShowBackgroundModal(false);
				setSelectedColor("#000000");
				setWidgets([]);
				setEventCards([]);
				setLoading(true);
				currentUidRef.current = uid;
			}

			const usersRef = collection(db, "users");
			const q = query(usersRef, where("uid", "==", uid));
			const querySnapshot = await getDocs(q);

		if (querySnapshot.empty) {
			setPageNotFound(true);
			setPageNotFoundReason('not_found');
			setLoading(false);
			return;
		}

		querySnapshot.forEach((docSnapshot) => {
			const userData = docSnapshot.data();
			const accountType = userData.accountType || '';
			const isGraduate = accountType === 'Graduate' || accountType === 'G' || accountType === 'admin';
			if (!isGraduate) {
				setPageNotFound(true);
				setPageNotFoundReason('wrong_account_type');
				setLoading(false);
				return;
			}

			const pageConfig = userData?.pages?.eventsPageConfig;

			if (pageConfig) {
				setPageOwner(userData);
					let fetchedCards = pageConfig?.cards || [];
					
					let needsRepair = false;
					const repairedCards = fetchedCards.map((c, i) => {
					if (c.type && typeof c.type !== 'string') {
						if (isDevelopment) console.warn(`🔧 Visitor page card ${i} has corrupted type (${typeof c.type}) — attempting auto-repair`);
						needsRepair = true;
						let repairedType = '';
						if (c.eventTitle || c.eventDate || c.eventLocation || c.rsvpEnabled) {
							repairedType = 'Event';
						} else if (c.images && Array.isArray(c.images)) {
							repairedType = c.images.length > 1 ? 'ImageGallery' : 'PictureFrame';
						} else if (c.url) {
							repairedType = 'URLPreview';
						} else if (c.content) {
							repairedType = 'TextBox';
						} else if (c.question) {
							repairedType = 'Poll';
						} else if (c.going || c.maybe || c.decline) {
							repairedType = 'RSVP';
						} else if (c.targetDate) {
							repairedType = 'Countdown';
						}
						
						if (repairedType) {
							if (isDevelopment) console.log(`✅ Auto-repaired visitor card ${i} type to: ${repairedType}`);
							return { ...c, type: repairedType };
						} else {
							if (isDevelopment) console.error(`❌ Cannot auto-repair visitor card ${i} — removing from array`);
							return null;
						}
					}
					return c;
				}).filter(c => c !== null);
				
				if (needsRepair) {
					if (isDevelopment) console.log('💾 Writing repaired cards back to Firestore...');
					const userDocRef = doc(db, "users", docSnapshot.id);
					updateDoc(userDocRef, {
						'pages.eventsPageConfig.cards': repairedCards
					}).then(() => {
						if (isDevelopment) console.log('✅ Corrupted widgets auto-repaired and saved on visitor page');
					}).catch(err => {
						if (isDevelopment) console.error('❌ Failed to save repaired visitor cards:', err);
					});
				}
					
					setCards(repairedCards.map((card) => ({ ...card })));

					const standardBackground = getProfileBackground(profileBackgroundChoices, pageConfig?.eventsStandardBackgroundKey);
					setSelectedStandardBackgroundKey(pageConfig?.eventsCustomBackground ? null : getBackgroundKey(standardBackground));
					setBackgroundImage(pageConfig?.eventsCustomBackground || getStandardProfileBackgroundValue(standardBackground));
					setApplyBlur(pageConfig?.eventsBlurEffect || false);
					const savedEventTextColor = pageConfig?.eventsTextColor || "#000000";
					const savedCardTextColor = userData.gradCardTextColor || "#FFFFFF";
					setTextColor(savedEventTextColor);
					setSelectedColor(savedCardTextColor);
					setSelectedColorSource(userData.gradCardTextColorSource || (getStandardFontColor(savedCardTextColor) ? "standard" : "custom"));
					setSlugInput(userData.myTasselPlus?.slug || "");

			} else {
				const standardBackground = defaultProfileBackground;
				setSelectedStandardBackgroundKey(getBackgroundKey(standardBackground));
				setBackgroundImage(getStandardProfileBackgroundValue(standardBackground));
				setPageOwner(userData);
				setApplyBlur(false);
				setTextColor("#000000");
				setSelectedColor(userData.gradCardTextColor || "#FFFFFF");
				setSelectedColorSource(userData.gradCardTextColorSource || (getStandardFontColor(userData.gradCardTextColor || "#FFFFFF") ? "standard" : "custom"));
				setSlugInput(userData.myTasselPlus?.slug || "");
				setCards([]);
			}
		});
	} catch (error) {
		// Silent fail for page config load
	} finally {
		setLoading(false);
	}
}, [db, navigation, alertShown, profileBackgroundChoices, defaultProfileBackground]);

	// Use useEffect to trigger save when cards state changes
	useEffect(() => {
		if (hasChanges) {
			if (isPreviewingAsOwner) {
				setHasChanges(false);
				return;
			}

			if (isDevelopment) console.log('⏱️ [useEffect hasChanges] hasChanges=true, cards:', cards.length, '→ triggering debouncedSave');
			debouncedSavePageConfig(cards, currentUser, db);
			setHasChanges(false);
		}
	}, [cards, hasChanges]);

	// Use useFocusEffect to run loadPageConfig when the screen is focused
	useFocusEffect(
		useCallback(() => {
			loadPageConfig();
		}, [loadPageConfig])
	);

	const loadUserData = async () => {
		if (isPageOwnerReady) {
			return;
		}

		try {
			const uid = await extractUIDFromURL();

			if (!uid) {
				return;
			}

			setPageOwnerId(uid);

			const usersRef = collection(db, "users");
			const querySnapshot = await getDocs(query(usersRef, where("uid", "==", uid)));

		if (querySnapshot.empty) {
			setPageNotFound(true);
			setPageNotFoundReason('not_found');
			setIsPageOwnerReady(true);
		} else {
			querySnapshot.forEach((doc) => {
				const userData = doc.data();
				const accountType = userData.accountType || '';
				const isGraduate = accountType === 'Graduate' || accountType === 'G' || accountType === 'admin';
				if (!isGraduate) {
					setPageNotFound(true);
					setPageNotFoundReason('wrong_account_type');
				}
				setPageOwner(userData);
				setIsPageOwnerReady(true);
			});
		}
	} catch (error) {
		// Silent fail for user data fetch
	}
};

	// Add a toggle edit function to enable the edit mode
	const toggleEditMode = () => {
		setEditableMode(!editableMode);
		if (editableMode && !isPreviewingAsOwner) {
			// If we are in edit mode, save the configuration
			debouncedSavePageConfig(cards, currentUser, db);
		} else if (editableMode) {
			showPreviewNotice("Closed owner preview editor. Changes are not saved.");
		}
	};

	// Change your renderEditButton function to use isOwner instead of editableMode:
	const renderEditButton = () => {
		const headerTutorials = ['friends', 'settings'];
		const shouldHideButtons = activePopupId && headerTutorials.includes(activePopupId);
		
		if (shouldHideButtons) {
			return null;
		}

		if ((isOwner || isAdminOwnerPreview) && !editableMode && !isVisitorView) {
			return (
				<View style={styles.ownerActionBar} pointerEvents="box-none">

					{/* Left: Owner Preview badge */}
					{isPreviewingAsOwner ? (
						<View style={{
							borderRadius: 20,
							backgroundColor: '#fefde2',
							borderWidth: 1,
							borderColor: 'rgba(76, 149, 108, 0.35)',
							flexDirection: 'row',
							overflow: 'hidden',
							shadowColor: '#000',
							shadowOffset: { width: 0, height: 2 },
							shadowOpacity: 0.12,
							shadowRadius: 4,
							elevation: 3,
							maxWidth: 300,
						}}>
							<View style={{ width: 5, backgroundColor: '#4c956c' }} />
							<View style={{ flex: 1, paddingVertical: 10, paddingHorizontal: 12, gap: 3 }}>
								<View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: 'rgba(76, 149, 108, 0.12)', borderWidth: 1, borderColor: 'rgba(76, 149, 108, 0.25)', alignSelf: 'flex-start' }}>
									<Text style={{ fontFamily: 'Bodoni72Smallcaps', fontSize: 10, color: '#2d5a3f', letterSpacing: 0.3 }}>Owner Preview</Text>
								</View>
								<Text style={{ fontFamily: 'Orpheus Pro', fontWeight: '600', fontSize: 13, color: '#2d5a3f' }} numberOfLines={1}>
									{ownerDisplayName}'s Events page
								</Text>
								<Text style={{ fontFamily: 'Orpheus Pro', fontSize: 11, color: '#4c6b57', opacity: 0.85 }}>
									Changes are local only
								</Text>
							</View>
						</View>
					) : (
						<View />
					)}

					{/* Right: tutorial dots + action buttons */}
					<View style={[styles.ownerActionBarButtons, isMobile && { flexDirection: 'column', alignItems: 'stretch' }]}>

						{/* View as Visitor */}
						<View style={{ position: 'relative' }}>
							<TutorialPopup
								id="eventsPreview"
								position={{ top: 0, right: 0 }}
								title="Preview Mode"
								openDirection="bottomLeft"
							/>
							<Pressable
								{...spotlightTarget(SPOTLIGHT_TARGETS.ANNOUNCEMENT_VIEW_AS_VISITOR)}
								onPress={() => setIsVisitorView(true)}
								style={({ pressed }) => [styles.viewButton]}
								onMouseEnter={() => setHoveredButton('viewAsVisitor')}
								onMouseLeave={() => setHoveredButton(null)}
							>
								<Text style={[styles.viewButtonText, isMobile && { marginHorizontal: 8 }]}>View as Visitor</Text>
							</Pressable>
						</View>

						{/* Edit Page Settings */}
						<View style={{ position: 'relative' }}>
							<TutorialPopup
								id="eventsBackground"
								position={{ top: 0, right: 0 }}
								title="Page Design"
								openDirection="bottomLeft"
								style={{ zIndex: 100010 }}
							/>
							<Pressable
								{...spotlightTarget(SPOTLIGHT_TARGETS.ANNOUNCEMENT_EDIT_PAGE_SETTINGS)}
								onPress={() => setShowBackgroundModal(true)}
								style={({ pressed }) => [styles.editButton]}
								onMouseEnter={() => setHoveredButton('editPageSettings')}
								onMouseLeave={() => setHoveredButton(null)}
							>
								<Text style={[styles.editButtonText, { color: '#4c956c' }, isMobile && { marginHorizontal: 8 }]}>Edit Page Settings</Text>
							</Pressable>
						</View>

					</View>
				</View>
			);
		} else if ((isOwner || isAdminOwnerPreview) && editableMode && !isVisitorView) {
			return (
				<View style={[styles.ownerActionBar, { justifyContent: 'flex-end' }]} pointerEvents="box-none">
					<Pressable style={styles.editButton} onPress={toggleEditMode}>
						<Text style={styles.editButtonText}>View Page</Text>
					</Pressable>
				</View>
			);
		}
	};

	//#region - Background Setting Logic
	// New function to render the Background Customization button
	const renderBackgroundButton = () => {
		if ((isOwner || isAdminOwnerPreview) && editableMode && !isVisitorView) {
			return (
				<Pressable style={styles.backgroundButton} onPress={() => setShowBackgroundModal(true)}>
					<Text style={styles.editButtonText}>Page Settings</Text>
				</Pressable>
			);
		}
	};

	const fileInputRef = useRef(null); // Add a ref for the file input

	// Function to handle background image upload
	const handleBackgroundUpload = async (event) => {
		if (!myTasselPlus.canUseFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND)) {
			event.target.value = '';
			myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND);
			return;
		}

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			if (isPreviewingAsOwner) {
				const previewUrl = typeof URL !== 'undefined' ? URL.createObjectURL(file) : null;
				if (previewUrl) setBackgroundImage(previewUrl);
				showPreviewNotice("Background updated in preview. Changes are not saved.");
				return;
			}

			const storageRef = ref(getStorage(app), `eventBackgrounds/${currentUser.uid}/${file.name}`);
			uploadBytes(storageRef, file)
				.then((snapshot) => {
					getDownloadURL(snapshot.ref).then((downloadURL) => {
						setSelectedStandardBackgroundKey(null);
						setBackgroundImage(downloadURL);
					});
				})
				.catch((error) => {
					// Upload failed silently
				});
		}
	};

	// Function to trigger the file input click
	const triggerFileInput = () => {
		myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND, () => {
			if (fileInputRef.current) {
				fileInputRef.current.click();
			}
		});
	};

	const selectStandardBackground = (background) => {
		setSelectedStandardBackgroundKey(getBackgroundKey(background));
		setBackgroundImage(getStandardProfileBackgroundValue(background));
	};

	const openCustomColor = () => {
		myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_COLORS, () => {
			setSelectedColorSource("custom");
			setShowCustomColorPicker((isVisible) => !isVisible);
		});
	};

	const handleStandardColorChange = (value) => {
		setSelectedColor(value);
		setSelectedColorSource("standard");
		setShowCustomColorPicker(false);
	};

	const handleCustomColorChange = (color) => {
		setSelectedColor(color.hex);
		setSelectedColorSource("custom");
	};

	const savePersonalizedUrl = async () => {
		const normalizedSlug = normalizeMyTasselPlusSlug(slugInput);
		setSlugInput(normalizedSlug);
		setSlugStatus({ checking: false, saving: true, available: false, error: "", success: "", changeAvailableAt: null, aliasExpiresAt: null });

		try {
			const result = await claimMyTasselPlusSlug(currentUser, normalizedSlug);
			const aliasExpirationText = formatPersonalizedUrlDate(result.previousSlugAliasExpiresAt);
			setSlugInput(result.slug || normalizedSlug);
			setPageOwner((currentOwner) => ({
				...currentOwner,
				myTasselPlus: {
					...(currentOwner?.myTasselPlus || {}),
					slug: result.slug || normalizedSlug,
					slugChangeAvailableAt: result.changeAvailableAt || currentOwner?.myTasselPlus?.slugChangeAvailableAt,
				},
			}));
			if (typeof window !== "undefined" && /^\/events\/[A-Za-z]\d{5}(?:$|[/?#])/.test(window.location.pathname)) {
				const vanityPath = `/${result.slug || normalizedSlug}`;
				window.history.replaceState(null, "", `${vanityPath}${window.location.search || ""}${window.location.hash || ""}`);
			}
			setSlugStatus({
				checking: false,
				saving: false,
				available: true,
				error: "",
				success: aliasExpirationText
					? `Your personalized URL has been updated. Your previous URL will continue working until ${aliasExpirationText}.`
					: "Personalized URL saved.",
				changeAvailableAt: result.changeAvailableAt || null,
				aliasExpiresAt: result.previousSlugAliasExpiresAt || null,
			});
		} catch (error) {
			setSlugStatus({
				checking: false,
				saving: false,
				available: false,
				error: error.message || "Could not save personalized URL.",
				success: "",
				changeAvailableAt: error.data?.changeAvailableAt || null,
				aliasExpiresAt: null,
			});
		}
	};

	// Function to apply background and color changes
	const applyChanges = async () => {
		if (isPreviewingAsOwner) {
			setShowBackgroundModal(false);
			showPreviewNotice("Page design preview updated. Changes are not saved.");
			return;
		}

		const userDocRef = doc(db, "users", currentUser.email.toLowerCase());

		const selectedStandardBackground = profileBackgroundChoices.find((bg) => (
			getStandardProfileBackgroundValue(bg) === backgroundImage ||
			getBackgroundKey(bg) === selectedStandardBackgroundKey
		));

		// SAFETY: Use updateDoc with dot-notation field paths so only these three display
		// fields are touched. setDoc({pages:{eventsPageConfig:{...}}}, {merge:true}) only
		// merges top-level document keys — it REPLACES the entire eventsPageConfig map,
		// wiping the cards array and all saved event data.
		try {
			await updateDoc(userDocRef, {
				'pages.eventsPageConfig.eventsCustomBackground': selectedStandardBackground ? null : backgroundImage,
				'pages.eventsPageConfig.eventsStandardBackgroundKey': getBackgroundKey(selectedStandardBackground) || selectedStandardBackgroundKey || getBackgroundKey(defaultProfileBackground),
				'pages.eventsPageConfig.eventsBlurEffect': applyBlur,
				gradCardTextColor: selectedColor,
				gradCardTextColorSource: selectedColorSource,
			});
			setPageOwner((currentOwner) => ({
				...currentOwner,
				gradCardTextColor: selectedColor,
				gradCardTextColorSource: selectedColorSource,
			}));
			setShowBackgroundModal(false);
		} catch (error) {
			if (isDevelopment) console.error('[applyChanges] Failed to save page settings:', error);
		}
	};

	// Function to toggle blur effect
	const toggleBlur = () => {
		setApplyBlur(!applyBlur);
	};

	// Function to revert to default background
	const revertToDefault = async () => {
		// Show confirmation dialog
		const confirmed = window.confirm(
			"Are you sure you want to revert to default settings? This will reset your background, text color, and blur effects to their original values."
		);
		
		if (!confirmed) return;

		if (isPreviewingAsOwner) {
			setSelectedStandardBackgroundKey(getBackgroundKey(defaultProfileBackground));
			setBackgroundImage(getStandardProfileBackgroundValue(defaultProfileBackground));
			setSelectedColor("#FFFFFF");
			setSelectedColorSource("standard");
			setApplyBlur(false);
			setShowBackgroundModal(false);
			showPreviewNotice("Default design preview applied. Changes are not saved.");
			return;
		}

		const userDocRef = doc(db, "users", currentUser.email.toLowerCase());

		// SAFETY: Use updateDoc with dot-notation so only the three display fields are
		// reset. Mirrors the same fix applied to applyChanges above.
		try {
			await updateDoc(userDocRef, {
				'pages.eventsPageConfig.eventsCustomBackground': null,
				'pages.eventsPageConfig.eventsStandardBackgroundKey': getBackgroundKey(defaultProfileBackground),
				'pages.eventsPageConfig.eventsBlurEffect': false,
				gradCardTextColor: '#FFFFFF',
				gradCardTextColorSource: 'standard',
			});
			
			// Update local state to reflect default settings
			setSelectedStandardBackgroundKey(getBackgroundKey(defaultProfileBackground));
			setBackgroundImage(getStandardProfileBackgroundValue(defaultProfileBackground));
			setSelectedColor("#FFFFFF");
			setSelectedColorSource("standard");
			setApplyBlur(false);
			setPageOwner((currentOwner) => ({
				...currentOwner,
				gradCardTextColor: "#FFFFFF",
				gradCardTextColorSource: "standard",
			}));
			
			setShowBackgroundModal(false);
			
			// Show success message
			alert("Successfully reverted to default settings!");
		} catch (error) {
			alert("Failed to revert to default settings. Please try again.");
		}
	};

	//#endregion

	//#endregion

	// #region - Widget Functions

	//#region - RSVP Functions
	const handleRSVPChange = (option, widgetIndex) => {
		setWidgets((currentWidgets) => currentWidgets.map((widget, index) => (index === widgetIndex && widget.type === "RSVP" ? { ...widget, rsvpResponse: option } : widget)));
		setHasChanges(true); // Mark that there are unsaved changes
	};
	//#endregion

	//#region - URLPreview Functions
	// Function to handle URL change
	const handleUrlChange = async (url, index) => {
		const updatedWidgets = [...widgets];
		updatedWidgets[index].url = url;
		setWidgets(updatedWidgets);
		setHasChanges(true); // Mark that there are unsaved changes
	};

	const handleImageUpload = async (event, index) => {
		const file = event.target.files[0];
		if (file && file.type.startsWith("image/")) {
			// Define the storage path
			const storagePath = `widgetsImages/${file.name}-${Date.now()}`;
			const storageRef = ref(getStorage(app), storagePath);

			try {
				// Upload the file to Firebase Storage
				const uploadResult = await uploadBytes(storageRef, file);
				// After upload, get the public URL
				const publicUrl = await getDownloadURL(uploadResult.ref);

				// Update the widget state with the new image URL
				setWidgets((currentWidgets) => {
					return currentWidgets.map((widget, widgetIndex) => {
						if (widgetIndex === index) {
							if (widget.type === "URLPreview" || widget.type === "PictureFrame") {
								// Update customImageUrl for URLPreview or images array for PictureFrame
								return {
									...widget,
									...(widget.type === "URLPreview" ? { customImageUrl: publicUrl } : { images: [...widget.images, publicUrl] }),
								};
							}
						}
						return widget;
					});
				});
				setHasChanges(true); // Mark that there are unsaved changes
			} catch (error) {
				// Silent fail for image upload
			}
		}
	};

	// Function to fetch metadata
	const fetchMetadata = async (index, cards, setCards) => {
		if (!cards || !cards[index] || !cards[index].url) {
			return;
		}
		const url = cards[index].url;

		try {
			const response = await axios.post(SERVER_URL, { url }, { headers: { "Content-Type": "application/json" } });
			const metadata = response.data;

			setCards((currentCards) => {
				const updatedCards = [...currentCards];
				updatedCards[index].metadata = metadata;
				return updatedCards;
			});
			setHasChanges(true);
		} catch (error) {
			// Silent fail for metadata fetch
		}
	};

	const renderFetchMetadataButton = (index) => {
		return (
			<Pressable style={styles.fetchMetadataButton} onPress={() => fetchMetadata(index, cards, setCards)}>
				<Text style={styles.buttonText}>Get URL</Text>
			</Pressable>
		);
	};
	//#endregion

	//#region - PictureFrame Functions
	const updatePictureFrameImages = (newImages, widgetIndex) => {
		setWidgets((currentWidgets) => {
			const updatedWidgets = currentWidgets.map((widget, index) => {
				if (index === widgetIndex && widget.type === "PictureFrame") {
					return {
						...widget,
						images: newImages,
						// Include any additional state related to layout, such as positions or rotations
					};
				}
				return widget;
			});
			setHasChanges(true); // Mark that there are unsaved changes
			return updatedWidgets;
		});
	};

	// Function to remove an image from the PictureFrame (by card ID)
	const removeImageFromPictureFrame = (cardId, imageUrl) => {
		setWidgets((currentWidgets) => {
			const updatedWidgets = currentWidgets.map((widget) => {
				if (widget.id === cardId && widget.type === "PictureFrame") {
					const updatedImages = widget.images.filter(image => image !== imageUrl);
					return {
						...widget,
						images: updatedImages,
					};
				}
				return widget;
			});
			setHasChanges(true);
			return updatedWidgets;
		});
	};

	//#endregion

	//#region - TextBox Functions
	const handleTextChange = (newText, widgetIndex) => {
		// Update the cards state instead of widgets state to prevent text loss
		setCards((currentCards) => {
			const updatedCards = [...currentCards];
			if (updatedCards[widgetIndex]?.type === "TextBox") {
				updatedCards[widgetIndex].text = newText;
			}
			setHasChanges(true); // Mark that there are unsaved changes
			return updatedCards;
		});
	};
	//#endregion

	//#endregion

	//#region - Initialization

	useEffect(() => {
		setTimeout(() => {
			loadUserData();
			loadPageConfig();
		}, 100);
	}, []);

	// Call this function within useEffect to check for ownership
	useEffect(() => {
		checkOwnership();
		// console.warn("currentUserData:", currentUserData);
	}, [currentUserData, pageOwnerId, isPageOwnerReady]);

	useEffect(() => {
		// Owner state updated
	}, [isOwner]);

	// Inside the useEffect that checks for page ownership
	const checkOwnership = () => {
		if (!currentUserData || !pageOwnerId) {
			setIsOwner(false);
			return;
		}

		const owner = currentUserData.uid === pageOwnerId;
		setIsOwner(owner);
		// console.log("Logged In User:", currentUserData, "isPageOwner:", owner);
	};

	// This code will run whenever `pageOwner` changes
	useEffect(() => {
		// Intentionally empty — initial load is handled by the useEffect([]) + useFocusEffect above.
		// Calling loadUserData/loadPageConfig here caused triple Firestore reads on every mount.
	}, [pageOwner]);

	useEffect(() => {
		// console.log("Cards Available in Hook:", cards, "Length:", cards.length);
	}, [cards]);

	const saveWidget = async (updatedCard) => {
		if (isPreviewingAsOwner) {
			setCards(currentCards => currentCards.map(c => c.id === updatedCard.id ? updatedCard : c));
			setHasChanges(false);
			showPreviewNotice("Widget updated in preview. Changes are not saved.");
			return;
		}

		const userEmail = currentUser.email.toLowerCase();
		const userDocRef = doc(db, "users", userEmail);
		const userDocSnap = await getDoc(userDocRef);
		const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
		const existingCards = existingData.pages?.eventsPageConfig?.cards || [];

		// Write-time enforcement: keep the needsReview flag accurate.
		// For Event cards, a title AND date are required minimum fields.
		// For other types, use WidgetWriteGuard's severity check.
		let cardToSave = updatedCard;
		if (updatedCard.type === 'Event') {
			const hasTitle = updatedCard.eventTitle?.trim();
			const hasDate = updatedCard.eventDate?.trim();
			if (!hasTitle || !hasDate) {
				cardToSave = { ...updatedCard, status: 'incomplete', needsReview: true };
			} else if (updatedCard.needsReview) {
				// Content is now present — clear the review flag.
				const { needsReview, ...rest } = updatedCard;
				cardToSave = { ...rest, status: 'active' };
			}
		} else {
			const { severity } = validateWidget(updatedCard);
			if (severity === 'critical' || severity === 'medium') {
				cardToSave = { ...updatedCard, status: 'incomplete', needsReview: true };
			} else if (updatedCard.needsReview) {
				const { needsReview, ...rest } = updatedCard;
				cardToSave = { ...rest, status: 'active' };
			}
		}

		// Persist the needsReview status flag to Firestore so the owner
		// sees the review notice even after a page reload.
		try {
			const firestoreCards = existingCards.map(c =>
				c.id === cardToSave.id
					? { ...c, status: cardToSave.status, needsReview: cardToSave.needsReview ?? false }
					: c
			);
			await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': firestoreCards });
		} catch (_) {
			// Non-critical — local state update still proceeds.
		}

		const updatedCards = existingCards.map(c => c.id === cardToSave.id ? cardToSave : c);
		setCards(updatedCards);
		setHasChanges(true);
	};

	useEffect(() => {
		// console.log("Render Cards:", cards, "Length:", cards.length);
	}, [cards]);

	//#region - Card Background Change Handler
	const handleBackgroundChange = (newBackground) => {
		setCardBackground(newBackground);
	};

	// Function to select a widget type and add it
	const selectWidget = (type) => {
		if (type && type !== "") {
			addCard(type);
		}
	};
	// #endregion

	// Add UIC Check and Authorization Logic
	useEffect(() => {
		const checkAuthorization = async () => {
			// Wait until both the page owner data AND the Firebase auth state have resolved
			// before making any access decision. This prevents a race where a private page
			// briefly flashes "unauthorized" while the admin's identity is still loading.
			if (!pageOwner || authLoading) {
				setIsAuthorizationLoading(true);
				return;
			}

			setIsAuthorizationLoading(true);

			const urlParams = new URLSearchParams(window.location.search);
			const uic = urlParams.get('uic');

			if (currentUserData?.accountType === 'admin') {
				// Admins can always access any page regardless of privacy settings.
				setIsAuthorized(true);
			} else if (uic && uic === pageOwner.uic) {
				setIsAuthorized(true);
			} else if (pageOwner.uid === currentUserData?.uid) {
				setIsAuthorized(true);
			} else if (!pageOwner.isFriendsListPrivate) {
				setIsAuthorized(true);
			} else if (currentUserData) {
				if (pageOwner.friends?.followers && pageOwner.friends.followers[currentUserData.uid]) {
					setIsAuthorized(true);
				} else {
					setIsAuthorized(false);
				}
			} else {
				setIsAuthorized(false);
			}

			setIsAuthorizationLoading(false); // End loading
		};

		checkAuthorization();
	}, [pageOwner, currentUserData, authLoading]);

	if (isAuthorizationLoading) {
		return (
			<View style={styles.container} {...spotlightTarget(SPOTLIGHT_TARGETS.ANNOUNCEMENT_PAGE)}>
				<ImageBackground source={require("../assets/Hub Background.png")} style={styles.backgroundImage}>
					<HeaderBar headerTranslateY={headerTranslateY} />
					<ScrollView
						contentContainerStyle={{ flexGrow: 1, paddingTop: isMobile ? HEADER_PADDING.mobile : isTablet ? HEADER_PADDING.tablet : HEADER_PADDING.desktop }}
						style={styles.body}
					>
						<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 80 }}>
							<ActivityIndicator size="large" color="#fefde2" />
						</View>
					</ScrollView>
				</ImageBackground>
			</View>
		);
	}

	if (pageNotFound) {
		return <PageUnavailableMessage reason={pageNotFoundReason} />;
	}

	// Don't show "private" until auth has fully resolved — prevents false flash for admins
	if (!isAuthorized && pageOwner && !authLoading) {
		return <PrivatePageMessage pageOwner={pageOwner} />;
	}

	// Example target date and message
	const targetDate = "2023-12-31T23:59:59"; // Replace with dynamic date if needed
	const onCompleteMessage = "The event has started!";
	const pageNoticeContainerStyle = {
		width: isMobile ? '92%' : '100%',
		maxWidth: 800,
		alignSelf: 'center',
		borderWidth: 1,
		borderRadius: 10,
		padding: 14,
		marginBottom: 14,
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: 10,
	};

	return (
		<>
			<SEOHead
				title={`${graduateName}'s Announcement Page | MyTassel`}
				description={`View and RSVP to ${graduateName}'s graduation events and celebrations`}
				image={pageOwner?.profilePictureURL}
				url={`https://mytassel.com/events/${pageOwnerId}`}
				type="profile"
			/>
			<EventStructuredData 
				event={{
					title: `${pageOwner?.firstName}'s Graduation Events`,
					description: `Graduation events and celebrations for ${pageOwner?.firstName} ${pageOwner?.lastName}`,
					startDate: pageOwner?.graduationDate,
					endDate: pageOwner?.graduationDate,
					location: {
						name: pageOwner?.organization?.name,
						address: pageOwner?.organization?.address
					}
				}}
			/>
			<View style={styles.container} {...spotlightTarget(SPOTLIGHT_TARGETS.ANNOUNCEMENT_PAGE)}>
				{/* Display loading, success, and error messages */}
				{loadingMessage && <Text style={styles.loadingMessage}>{loadingMessage}</Text>}
				{successMessage && <Text style={styles.successMessage}>{successMessage}</Text>}
				{errorMessage && <Text style={styles.errorMessage}>{errorMessage}</Text>}

				<ImageBackground 
					source={
						backgroundImage 
							? (typeof backgroundImage === 'string' && backgroundImage.startsWith('http') 
								? { uri: backgroundImage } 
								: backgroundImage)
							: require("../assets/Hub Background.png")
					} 
				style={styles.backgroundImage} 
				blurRadius={applyBlur ? 10 : undefined}
				>
					{/* Apply blur effect based on state */}
					{/* Header Bar */}
					<HeaderBar headerTranslateY={headerTranslateY} />

					<ScrollView
						ref={scrollViewRef}
						onScroll={(e) => {
							if (e && e.nativeEvent && e.nativeEvent.contentOffset) {
								scrollOffsetRef.current = e.nativeEvent.contentOffset.y;
							} else if (typeof window !== 'undefined') {
								scrollOffsetRef.current = window.pageYOffset || document.documentElement.scrollTop || 0;
							}
							onScroll(e);
						}}
						scrollEventThrottle={16}
						contentContainerStyle={{ flexGrow: 1, paddingTop: isMobile ? HEADER_PADDING.mobile : isTablet ? HEADER_PADDING.tablet : HEADER_PADDING.desktop }}
						style={styles.body}
					>
					{isOwner && showTutorial && (
						<>
							{/* Events Page Intro Tutorial */}
							<TutorialPopup
								id="eventsIntro"
								position={{
									top: isMobile ? 175 : isSmallDesktop ? 150 : 175,
									left: isMobile ? 75 : isSmallDesktop ? 50 : 75
								}}
								title="Events Page"
								openDirection="bottomRight"
							/>
						</>
					)}

						{/* Edit Button */}
					{(isOwner || isAdminOwnerPreview) && renderEditButton()}

					{/* Incomplete-widget review banner — visible only to the page owner */}
					{(isOwner || isAdminOwnerPreview) && !isVisitorView && cards.some(c => c.needsReview) && (
						<View style={{
							...pageNoticeContainerStyle,
							backgroundColor: '#fef3c7',
							borderColor: '#f59e0b',
						}}>
							<Text style={{ fontSize: 18 }}>⚠️</Text>
							<View style={{ flex: 1 }}>
								<Text style={{ fontWeight: '700', color: '#92400e', fontSize: 14, marginBottom: 4 }}>
									{cards.filter(c => c.needsReview).length === 1
										? '1 event widget needs your attention'
										: `${cards.filter(c => c.needsReview).length} event widgets need your attention`}
								</Text>
								<Text style={{ color: '#78350f', fontSize: 13 }}>
									This event has missing information and needs review. Please edit the highlighted widget{cards.filter(c => c.needsReview).length !== 1 ? 's' : ''} to provide complete details.
								</Text>
							</View>
						</View>
					)}

					{/* Customization Button */}
					{renderBackgroundButton()}

					{/* Countdown Component */}
					{/* <Countdown targetDate={targetDate} onCompleteMessage={onCompleteMessage} /> */}

					{/* Background Customization Modal */}
					{showBackgroundModal && (
						<Modal transparent={true} animationType="slide" visible={showBackgroundModal} onRequestClose={() => setShowBackgroundModal(false)}>
							<View style={styles.modalContainer}>
								<View style={styles.modalContent} {...spotlightTarget(SPOTLIGHT_TARGETS.PAGE_SETTINGS_MODAL)}>
									<Pressable style={styles.modalCloseButton} onPress={() => setShowBackgroundModal(false)}>
										<Text style={styles.modalCloseButtonText}>X</Text>
									</Pressable>
									<ScrollView style={styles.settingsModalScroll} contentContainerStyle={styles.settingsModalContent}>
										<Text style={styles.modalTitle}>Profile Settings</Text>

										<View style={styles.settingsSection} {...spotlightTarget(SPOTLIGHT_TARGETS.PAGE_SETTINGS_BACKGROUND)}>
											<Text style={styles.settingsSectionTitle}>Background</Text>
											<ScrollView horizontal={true} showsHorizontalScrollIndicator={false} contentContainerStyle={styles.backgroundThumbRow}>
												{profileBackgroundChoices.map((bg) => (
													<Pressable
														key={getBackgroundKey(bg)}
														onPress={() => selectStandardBackground(bg)}
														style={[
															styles.backgroundChoice,
															selectedStandardBackgroundKey === getBackgroundKey(bg) && styles.backgroundChoiceActive,
														]}
													>
														<Image style={styles.backgroundThumbnail} source={getImageSource(getStandardProfileBackgroundValue(bg))} />
														<Text style={styles.backgroundChoiceLabel}>{bg.label}</Text>
													</Pressable>
												))}
											</ScrollView>

											<Pressable style={styles.secondarySettingsButton} onPress={triggerFileInput}>
												<Text style={styles.secondarySettingsButtonText}>Upload Custom Background {!myTasselPlus.hasPlus ? "+" : ""}</Text>
											</Pressable>
										</View>

										<View style={styles.settingsSection} {...spotlightTarget(SPOTLIGHT_TARGETS.PAGE_SETTINGS_TEXT_COLOR)}>
											<Text style={styles.settingsSectionTitle}>Text Color</Text>
											<View style={styles.activeColorSummary}>
												<View style={[styles.activeColorSwatch, { backgroundColor: selectedColor }]} />
												<View>
													<Text style={styles.activeColorValue}>{selectedColor.toUpperCase()}</Text>
													<Text style={styles.activeColorSource}>
														{selectedColorSource === "custom" ? "Custom color active" : "Standard color active"}
													</Text>
												</View>
											</View>

											<View style={[styles.colorSourcePanel, selectedColorSource === "standard" && styles.colorSourcePanelActive]}>
												<View style={styles.standardColorSwatchRow}>
													{STANDARD_FONT_COLORS.map((color) => (
														<Pressable
															key={color.value}
															onPress={() => handleStandardColorChange(color.value)}
															style={[
																styles.standardColorChip,
																selectedColorSource === "standard" && selectedColor.toLowerCase() === color.value.toLowerCase() && styles.standardColorChipActive,
															]}
														>
															<View style={[styles.standardColorChipSwatch, { backgroundColor: color.value }]} />
															<Text style={styles.standardColorChipText}>{color.label}</Text>
														</Pressable>
													))}
												</View>
											</View>

											<View style={[styles.colorSourcePanel, selectedColorSource === "custom" && styles.colorSourcePanelActive]}>
												<Pressable style={styles.secondarySettingsButton} onPress={openCustomColor}>
													<Text style={styles.secondarySettingsButtonText}>Custom Color {!myTasselPlus.hasPlus ? "+" : ""}</Text>
												</Pressable>
												{showCustomColorPicker && myTasselPlus.hasPlus && (
													<View style={styles.customColorPickerWrap}>
														<SketchPicker
															color={selectedColor}
															onChangeComplete={handleCustomColorChange}
															onChange={handleCustomColorChange}
														/>
													</View>
												)}
											</View>
										</View>

										<View style={styles.settingsSection} {...spotlightTarget(SPOTLIGHT_TARGETS.PAGE_SETTINGS_URL)}>
											<Text style={styles.settingsSectionTitle}>Personalized URL</Text>
											<Text style={styles.publicUrlPreview}>{publicUrlPreview}</Text>
											<View style={styles.legacyUrlNoteBox}>
												<Text style={styles.legacyUrlNoteText}>
													Your original MyTassel links will always continue to work. Your personalized URL simply gives you a shorter link to share.
												</Text>
												<Text style={styles.legacyUrlLine}>Announcement: {legacyAnnouncementPath}</Text>
												<Text style={styles.legacyUrlLine}>Registry: {legacyRegistryPath}</Text>
											</View>
											{myTasselPlus.hasPlus ? (
												<>
													<TextInput
														value={slugInput}
														onChangeText={(value) => {
															setSlugInput(value);
															setSlugStatus({ checking: false, saving: false, available: false, error: "", success: "", changeAvailableAt: null, aliasExpiresAt: null });
														}}
														autoCapitalize="none"
														autoCorrect={false}
														placeholder="your-name"
														placeholderTextColor="#777"
														style={[
															styles.slugInput,
															slugStatus.error && styles.slugInputError,
															slugStatus.available && styles.slugInputAvailable,
														]}
													/>
													{isChangingSavedSlug ? (
														<Text style={styles.slugPolicyText}>
															You can change your personalized URL once every 30 days. Your previous URL will continue working for 14 days after a change.
														</Text>
													) : null}
													{slugStatus.checking ? <Text style={styles.slugCheckingText}>Checking availability...</Text> : null}
													{slugStatus.error ? <Text style={styles.slugErrorText}>{slugStatus.error}</Text> : null}
													{slugStatus.success ? <Text style={styles.slugSuccessText}>{slugStatus.success}</Text> : null}
													<Pressable
														style={[
															styles.applyButton,
															(!normalizedSlugPreview || slugStatus.checking || slugStatus.saving || Boolean(slugStatus.error)) && styles.settingsButtonDisabled,
														]}
														onPress={savePersonalizedUrl}
														disabled={!normalizedSlugPreview || slugStatus.checking || slugStatus.saving || Boolean(slugStatus.error)}
													>
														<Text style={styles.applyButtonText}>{slugStatus.saving ? "Saving..." : "Save Personalized URL"}</Text>
													</Pressable>
												</>
											) : (
												<Pressable style={styles.secondarySettingsButton} onPress={() => myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.PERSONALIZED_URL)}>
													<Text style={styles.secondarySettingsButtonText}>Unlock Personalized URL +</Text>
												</Pressable>
											)}
										</View>

										<View style={styles.settingsSection} {...spotlightTarget(SPOTLIGHT_TARGETS.PAGE_SETTINGS_BLUR)}>
											<View style={styles.blurToggle}>
												<Text style={styles.settingsSectionTitle}>Background Blur</Text>
												<Switch value={applyBlur} onValueChange={toggleBlur} />
											</View>
										</View>

										<View style={styles.settingsButtonRow} {...spotlightTarget(SPOTLIGHT_TARGETS.PAGE_SETTINGS_ACTIONS)}>
											<Pressable style={styles.applyButton} onPress={() => applyChanges()}>
												<Text style={styles.applyButtonText}>Apply Changes</Text>
											</Pressable>

											<Pressable style={styles.resetSettingsButton} onPress={() => revertToDefault()}>
												<Text style={styles.applyButtonText}>Revert to Default</Text>
											</Pressable>
										</View>
									</ScrollView>

								</View>
							</View>
						</Modal>
					)}

					<View style={styles.pageHeader}>
						<View style={styles.profileContainer}>
						{loading || !pageOwner ? (
							<View style={{ justifyContent: 'center', alignItems: 'center', marginTop: isMobile ? 100 : 200, paddingVertical: 80 }}>
								<ActivityIndicator size="large" color="#fefde2" />
							</View>
						) : (
							<BackgroundProvider pageOwner={pageOwner}>
								<>
								<View {...spotlightTarget(SPOTLIGHT_TARGETS.ANNOUNCEMENT_GRADUATION_CARD)} style={[styles.graduationCardContainer, !isMobile && !((isOwner || isAdminOwnerPreview) && !isVisitorView) && { marginTop: 200 }, isMobile && (isOwner || isAdminOwnerPreview) && !isVisitorView && { marginTop: 280 }]}>
									<GraduationCard pageOwner={pageOwner} currentUserData={currentUserData} isVisitorView={isVisitorView} />
								</View>
							</>

							<CombinedCard pageOwnerId={pageOwnerId} pageOwner={pageOwner} isVisitorView={isVisitorView} isPreviewMode={isPreviewingAsOwner} />
					{/* Render Cards */}
					{renderCards()}

									{/* Widget picker shown inline when no cards exist */}
									{(isOwner || isAdminOwnerPreview) && !isVisitorView && cards.length === 0 && (() => {
										const widgetOptions = getAvailableWidgetOptions(featureFlags);
										// Calculate spacer height to prevent white gap when dropdown is open
										const dropdownHeight = widgetOptions.length * 48 + 20;
										return (
											<View {...spotlightTarget(SPOTLIGHT_TARGETS.WIDGET_PICKER)} style={{ position: 'relative', zIndex: 100, alignSelf: 'center', width: isMobile ? '90%' : '50%', marginTop: 16, marginBottom: showEmptyWidgetDropdown ? dropdownHeight + 16 : 16 }}>
												<Pressable
													onPress={isAddingWidget ? null : () => setShowEmptyWidgetDropdown(!showEmptyWidgetDropdown)}
													disabled={isAddingWidget}
													style={({ hovered }) => ({
														flexDirection: 'row',
														alignItems: 'center',
														justifyContent: 'space-between',
														backgroundColor: (!isMobile && hovered && !isAddingWidget) ? 'rgba(254, 253, 226, 1)' : 'rgba(254, 253, 226, 0.95)',
														borderRadius: 14,
														paddingHorizontal: 16,
														paddingVertical: 12,
														borderWidth: 1,
														borderColor: showEmptyWidgetDropdown ? 'rgba(76, 149, 108, 0.4)' : 'rgba(76, 149, 108, 0.15)',
														shadowColor: '#1a5c3a',
														shadowOffset: { width: 0, height: 2 },
														shadowOpacity: isAddingWidget ? 0.5 : 0.08,
														shadowRadius: 6,
														elevation: 2,
														opacity: isAddingWidget ? 0.6 : 1,
														cursor: isAddingWidget ? 'not-allowed' : 'pointer',
													})}
												>
													<Text style={{ fontSize: 15, fontWeight: '600', color: '#999', fontFamily: 'Bodoni72Smallcaps' }}>
														{isAddingWidget ? 'Adding widget...' : 'Select a widget...'}
													</Text>
													<Text style={{ fontSize: 12, color: '#4c956c', fontFamily: 'Bodoni72Smallcaps' }}>{showEmptyWidgetDropdown ? '▲' : '▼'}</Text>
												</Pressable>

												{showEmptyWidgetDropdown && (
													<View style={{
														position: 'absolute',
														top: '100%',
														left: 0,
														right: 0,
														marginTop: 4,
														backgroundColor: '#fff',
														borderRadius: 14,
														borderWidth: 1,
														borderColor: 'rgba(76, 149, 108, 0.15)',
														shadowColor: '#1a5c3a',
														shadowOffset: { width: 0, height: 8 },
														shadowOpacity: 0.12,
														shadowRadius: 16,
														elevation: 8,
														overflow: 'hidden',
														zIndex: 999,
													}}>
														{widgetOptions.map((option, idx) => (
															<Pressable
																key={option.value}
																{...spotlightTarget(widgetOptionTarget(option.value))}
																onPress={isAddingWidget ? null : () => {
																	emitSpotlightEvent(SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_SELECTED, { type: option.value });
																	selectWidget(option.value);
																	setShowEmptyWidgetDropdown(false);
																}}
																disabled={isAddingWidget}
																style={({ hovered }) => ({
																	flexDirection: 'row',
																	alignItems: 'center',
																	gap: 12,
																	paddingHorizontal: 16,
																	paddingVertical: 12,
																	backgroundColor: (!isMobile && hovered && !isAddingWidget) ? 'rgba(254, 253, 226, 0.5)' : 'transparent',
																	opacity: isAddingWidget ? 0.5 : 1,
																	cursor: isAddingWidget ? 'not-allowed' : 'pointer',
																	borderBottomWidth: idx < widgetOptions.length - 1 ? 1 : 0,
																	borderBottomColor: 'rgba(76, 149, 108, 0.06)',
																})}
															>
																<View style={{ flex: 1 }}>
																	<Text style={{ fontSize: 14, fontWeight: '600', color: '#2d5a3f', fontFamily: 'Bodoni72Smallcaps' }}>{option.label}</Text>
																	<Text style={{ fontSize: 11, color: '#999', fontFamily: 'Orpheus Pro' }}>{option.desc}</Text>
																</View>
															</Pressable>
														))}
													</View>
												)}
											</View>
										);
									})()}

							</BackgroundProvider>
						)}
						</View>

					</View>


					</ScrollView>
				</ImageBackground>

				{/* Empty-state widget dropdown is now rendered inline inside BackgroundProvider above */}

				{/* Edit Mode Controls and Widget Menu */}
				{editableMode && (
					<>
						{/* Horizontally Scrollable Widget Menu */}
						<ScrollView horizontal={true} showsHorizontalScrollIndicator={false} style={styles.widgetMenuScrollView}>
							<View style={styles.widgetMenu}>
								<View style={styles.widgetSpacer}></View>
								<Pressable style={styles.widgetButton} onPress={() => selectWidget("TextBox")}>
									<Text style={styles.prevText}>Text Box</Text>
									<TextBox style={styles.previewStyle} textColor={selectedColor} />
								</Pressable>

								<Pressable style={styles.widgetButton} onPress={() => selectWidget("ImageGallery")}>
									<Text style={styles.prevText}>Image Gallery</Text>
									<ImageGallery isPreview={true} style={styles.previewStyle} />
								</Pressable>

								<Pressable style={styles.widgetButton} onPress={() => selectWidget("RSVP")}>
									<Text style={styles.prevText}>RSVP</Text>
									<RSVP style={styles.previewStyle} />

								</Pressable>

								<Pressable style={styles.widgetButton} onPress={() => selectWidget("PictureFrame")}>
									<Text style={styles.prevText}>Picture Frame</Text>
									<PictureFrame style={styles.previewStyle} />
								</Pressable>

								<Pressable style={styles.widgetButton} onPress={() => selectWidget("URLPreview")}>
									<Text style={styles.prevText}>URL Preview</Text>
									<URLPreview style={styles.previewStyle} />
								</Pressable>

								<Pressable style={styles.widgetButton} onPress={() => selectWidget("Countdown")}>
									<Text style={styles.prevText}>Countdown</Text>
									<Countdown style={styles.previewStyle} />
								</Pressable>

						{featureFlags.showPollsWidget && (
						<Pressable style={styles.widgetButton} onPress={() => selectWidget("Poll")}>
							<Text style={styles.prevText}>Poll</Text>
							<View style={styles.previewStyle}>
								<Text style={{ fontSize: 12, textAlign: 'center', color: '#4c956c' }}>📊 Poll</Text>
							</View>
						</Pressable>
						)}


							{isOwner && showTutorial && (
								<TutorialPopup
									id="eventsSave"
									position={{
										bottom: isMobile ? 70 : 80,
										right: isMobile ? 10 : 20,
										zIndex: 100000
									}}
									title="Save Changes"
									openDirection="topLeft"
									style={{ zIndex: 100000 }}
								/>
							)}

							<Pressable style={styles.cancelButton} onPress={() => debouncedSavePageConfig(cards, currentUser, db)}>
								<Text style={styles.editButtonText}>Save</Text>
							</Pressable>
							<Pressable style={styles.cancelButton} onPress={() => setEditableMode(false)}>
								<Text style={styles.cancelButtonText}>Close</Text>
							</Pressable>
							{/* Add other controls as needed */}
						</View>
					</ScrollView>
					</>
				)}
				{isVisitorView && <VisitorViewBanner onReturn={() => setIsVisitorView(false)} />}
				{/* Hidden file input */}
				<input
					type="file"
					ref={fileInputRef}
					style={{ display: 'none' }}
					onChange={handleBackgroundUpload}
				/>
				{myTasselPlus.upsellModal}
			</View>
		</>
	);
};

export default Events;
